import { useMemo } from 'react';

import TimelineClientMessage from 'src/features/product/product-detail/ui/timeline/TimelineClientMessage';
import { EntityTimeline, ExpectedTag } from 'src/shared/components';
import { Sampling } from 'src/shared/types/sampling';

import LabelValueColumn from 'src/shared/components/LabelValueColumn';
import { Typography } from 'src/shared/components/ui';

import {
  STAGE_ICONS,
  STAGE_LABELS,
  STAGE_TO_GROUP_MAP
} from '../../constants/samplingTimeline';
import getDeliveredDate from '../../utils/getDeliveredDate';

interface SamplingModalTimelineProps {
  sampling: Sampling | null;
  isOpen: boolean;
}

const SamplingModalTimeline: React.FC<SamplingModalTimelineProps> = ({
  sampling,
  isOpen
}) => {
  const currentStage = useMemo(() => {
    if (!sampling?.timeline) return null;
    const lastIndex = sampling?.timeline.findLastIndex(
      (value) => value.stage === sampling?.stage
    );

    if (lastIndex === -1) return null;

    return {
      ...sampling?.timeline[lastIndex],
      timelineIndex: lastIndex
    };
  }, [sampling?.timeline, sampling?.stage]);

  const hasDeliveredStage =
    sampling?.timeline?.some((t) => t.stage === 'delivered') ?? false;
  const deliveredDate =
    hasDeliveredStage && sampling ? getDeliveredDate(sampling) : null;

  return (
    <div className="flex flex-col">
      <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
        <Typography variant="h5" weight="bold">
          Overview
        </Typography>

        <div className="grid grid-cols-2 gap-5">
          <LabelValueColumn
            label="Current stage"
            value={STAGE_LABELS[sampling?.stage || ''] || sampling?.stage}
            valueVariant="h4"
            valueWeight="bold"
          />
          {deliveredDate && (
            <LabelValueColumn
              label="Sample in hand"
              value={
                <div className="flex items-center gap-1">
                  <Typography variant="h4" weight="bold">
                    {deliveredDate.date}
                  </Typography>
                  {deliveredDate.is_expected && <ExpectedTag />}
                </div>
              }
              valueVariant="h4"
              valueWeight="bold"
            />
          )}
        </div>

        {currentStage?.client_notes && (
          <TimelineClientMessage stage={currentStage} />
        )}
      </div>

      <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
        <Typography variant="h5" weight="bold">
          Progress
        </Typography>

        {sampling?.timeline && currentStage && (
          <EntityTimeline
            timeline={sampling?.timeline}
            currentStage={currentStage}
            isOpen={isOpen}
            stageIcons={STAGE_ICONS}
            stageLabels={STAGE_LABELS}
            stageToGroupMap={STAGE_TO_GROUP_MAP}
          />
        )}
      </div>
    </div>
  );
};

export default SamplingModalTimeline;
